// [Phase 6a foundation] Example domain query hooks built on TanStack
// Query + the typed ApiClient. New screens add hooks here so the
// query-key vocabulary stays in one place (drives invalidation, prefetch,
// devtools labelling).
//
// Pattern:
//   1. Define a stable `queryKey` const so callers can invalidate by
//      key tuple.
//   2. Define a hook that pulls the api client from useApi() and
//      delegates to useQuery.
//   3. Re-export the response type so callers can name it.
import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

// ────────────────────────────────────────────────────────────────────────
// /tenants/me/activity — drives the Home dashboard "Recent activity"
// card and the future Activity feed (S-061d).
// ────────────────────────────────────────────────────────────────────────

export type ActivityRow = {
  id: string;
  occurredAt: string;
  toolName: string;
  layerSource: "L1" | "L2" | "both";
  title: string;
  meta: string;
  actor: { uid: string; displayName: string };
};

export const ACTIVITY_KEY = ["tenant", "activity"] as const;

export function useRecentActivity(
  limit = 10
): UseQueryResult<ActivityRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...ACTIVITY_KEY, limit],
    queryFn: async () => {
      // Server returns the paginated shape `{ rows, nextCursor }`; the
      // Home card cares about a small fixed slice and never paginates,
      // so we discard the cursor.
      const page = await api<{ rows: ActivityRow[]; nextCursor: string | null }>(
        `/tenants/me/activity?limit=${limit}`
      );
      return page.rows;
    },
  });
}

// ────────────────────────────────────────────────────────────────────────
// /tenants/me/activity (paginated + filtered) — drives the full Activity
// feed page (S-061d). Distinct from useRecentActivity because the home
// card cares about a small fixed slice and never paginates.
// ────────────────────────────────────────────────────────────────────────

export type ActivitySourceFilter = "all" | "L1" | "L2" | "both";

export type ActivityFeedFilter = {
  source: ActivitySourceFilter;
  /** ISO datetime — only rows after this point. Default 7 days back. */
  since?: string;
  /** Optional substring match on actor uid. */
  actor?: string;
  /** Optional toolName equality. */
  toolName?: string;
};

export type ActivityFeedPage = {
  rows: ActivityRow[];
  nextCursor: string | null;
};

export const ACTIVITY_FEED_KEY = ["tenant", "activity-feed"] as const;

export function useActivityFeed(
  filter: ActivityFeedFilter,
  cursor: string | null = null,
  limit = 50
): UseQueryResult<ActivityFeedPage, Error> {
  const api = useApi("admin");
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor !== null) params.set("cursor", cursor);
  if (filter.since !== undefined) params.set("since", filter.since);
  return useQuery({
    queryKey: [...ACTIVITY_FEED_KEY, filter, cursor, limit],
    // keep prior rows visible while a filter/scope change refetches (no blank flash)
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const page = await api<ActivityFeedPage>(
        `/tenants/me/activity?${params.toString()}`
      );
      return {
        rows: applyClientFilters(page.rows, filter),
        nextCursor: page.nextCursor,
      };
    },
  });
}

function applyClientFilters(
  rows: ActivityRow[],
  filter: ActivityFeedFilter
): ActivityRow[] {
  return rows.filter((row) => {
    if (filter.source !== "all" && row.layerSource !== filter.source) {
      return false;
    }
    if (
      filter.actor !== undefined &&
      filter.actor.length > 0 &&
      !row.actor.uid.toLowerCase().includes(filter.actor.toLowerCase())
    ) {
      return false;
    }
    if (
      filter.toolName !== undefined &&
      filter.toolName.length > 0 &&
      row.toolName !== filter.toolName
    ) {
      return false;
    }
    return true;
  });
}

// ────────────────────────────────────────────────────────────────────────
// [S-250 / S-251] useIndexHealth — real backend snapshot from
// GET /admin/health/index. The endpoint aggregates live counts from
// Firestore (templates by lifecycle state); document pipeline and
// Layer 2 numbers are zero until those backends ship (S-093 / S-181).
// The shape stays stable so the IndexHealth screen renders identically
// against the live data.
// ────────────────────────────────────────────────────────────────────────

export type LifecycleCount = { state: string; count: number };

export type IndexHealthSnapshot = {
  generatedAt: string;
  certifiedLibrary: {
    totalTemplates: number;
    certifiedAndCurrent: number;
    awaitingReattestation: number;
    lifecycle: LifecycleCount[];
    lastSyncAt: string | null;
  };
  studioPrecedent: {
    documentsIndexed: number;
    deals: number;
    ingested24h: number;
    lifecycle: LifecycleCount[];
    lastIndexedAt: string | null;
    dlqDepth: number;
  };
  health: {
    readApi: "connected" | "degraded" | "disconnected";
    layer2Client: "connected" | "degraded" | "disconnected";
    lastAuditEmitSecondsAgo: number | null;
  };
};

export const INDEX_HEALTH_KEY = ["index-health"] as const;

// Retained only as the structural fallback when the endpoint errors
// out — the hook below switches to it if the network round-trip fails,
// so the screen still renders.
const FALLBACK_SNAPSHOT: IndexHealthSnapshot = {
  generatedAt: new Date(0).toISOString(),
  certifiedLibrary: {
    totalTemplates: 0,
    certifiedAndCurrent: 0,
    awaitingReattestation: 0,
    lifecycle: [],
    lastSyncAt: null,
  },
  studioPrecedent: {
    documentsIndexed: 0,
    deals: 0,
    ingested24h: 0,
    lifecycle: [],
    lastIndexedAt: null,
    dlqDepth: 0,
  },
  health: {
    readApi: "disconnected",
    layer2Client: "disconnected",
    lastAuditEmitSecondsAgo: null,
  },
};

export function useIndexHealth(): UseQueryResult<IndexHealthSnapshot, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: INDEX_HEALTH_KEY,
    queryFn: () => api<IndexHealthSnapshot>("/admin/health/index"),
    staleTime: 60_000,
  });
}

// Exported only so IndexHealth.tsx can render a useful empty/error
// state without crashing on undefined data. Intentionally not a public
// API.
export const INDEX_HEALTH_FALLBACK = FALLBACK_SNAPSHOT;
